import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';

const UNIDADES = ['qq', 'unidades', 'litros', 'dosis', 'sacos', 'meses', 'dias', 'global'];

export const GastoDialog = ({
  open, onOpenChange, editing,
  form, onChange, onMontoCalc,
  categorias, saving, onSave,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="modal-gasto">
      <DialogHeader>
        <DialogTitle>{editing ? 'Editar Gasto' : 'Registrar Gasto'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Categoria *</Label>
            <Select value={form.categoria} onValueChange={(v) => onChange({ ...form, categoria: v })}>
              <SelectTrigger className="h-11 mt-1.5">
                <SelectValue placeholder="Seleccione" />
              </SelectTrigger>
              <SelectContent>
                {categorias.map((c) => (
                  <SelectItem key={c.id || c.nombre} value={c.nombre}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fecha *</Label>
            <Input
              type="date"
              value={form.fecha}
              onChange={(e) => onChange({ ...form, fecha: e.target.value })}
              className="h-11 mt-1.5"
            />
          </div>
        </div>
        <div>
          <Label>Descripcion</Label>
          <Input
            placeholder="Ej: Compra de 10 sacos de alimento"
            value={form.descripcion}
            onChange={(e) => onChange({ ...form, descripcion: e.target.value })}
            className="h-11 mt-1.5"
          />
        </div>
        <Separator />
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Cantidad</Label>
            <Input
              type="number" inputMode="decimal" step="0.01" placeholder="0"
              value={form.cantidad}
              onChange={(e) => onMontoCalc('cantidad', e.target.value)}
              className="h-10 mt-1 tabular-nums"
            />
          </div>
          <div>
            <Label className="text-xs">Unidad</Label>
            <Select value={form.unidad} onValueChange={(v) => onChange({ ...form, unidad: v })}>
              <SelectTrigger className="h-10 mt-1">
                <SelectValue placeholder="--" />
              </SelectTrigger>
              <SelectContent>
                {UNIDADES.map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Precio/u ($)</Label>
            <Input
              type="number" inputMode="decimal" step="0.01" placeholder="0.00"
              value={form.precio_unitario}
              onChange={(e) => onMontoCalc('precio_unitario', e.target.value)}
              className="h-10 mt-1 tabular-nums"
            />
          </div>
        </div>
        <div>
          <Label>Monto Total *</Label>
          <Input
            type="number" inputMode="decimal" step="0.01" placeholder="0.00"
            value={form.monto}
            onChange={(e) => onChange({ ...form, monto: e.target.value })}
            className="h-11 mt-1.5 font-semibold tabular-nums text-lg"
          />
          <p className="text-xs text-muted-foreground mt-0.5">
            Se calcula automaticamente si ingresa cantidad × precio
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
        <Button onClick={onSave} disabled={saving} data-testid="btn-guardar-gasto">
          {saving
            ? (editing ? 'Actualizando...' : 'Guardando...')
            : (editing ? 'Actualizar Gasto' : 'Guardar Gasto')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ConfigCostosDialog = ({ open, onOpenChange, form, onChange, saving, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="modal-config-costos">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Configuracion de Costos</DialogTitle>
        <CardDescription>
          Configure los parametros para calcular depreciacion y costos automaticos de alimento.
        </CardDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <h4 className="text-sm font-semibold text-muted-foreground">DEPRECIACIÓN DE GRANJA</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Valor de la Granja ($)</Label>
            <Input
              type="number" inputMode="decimal" step="0.01" placeholder="0.00"
              value={form.valor_granja}
              onChange={(e) => onChange({ ...form, valor_granja: e.target.value })}
              className="h-11 mt-1.5"
            />
          </div>
          <div>
            <Label>Vida Util (años)</Label>
            <Input
              type="number" inputMode="numeric" placeholder="20"
              value={form.vida_util_anos}
              onChange={(e) => onChange({ ...form, vida_util_anos: e.target.value })}
              className="h-11 mt-1.5"
            />
          </div>
        </div>
        <div>
          <Label>N° de lotes que comparten depreciacion</Label>
          <Input
            type="number" inputMode="numeric" min="1" placeholder="1"
            value={form.num_lotes_depreciar}
            onChange={(e) => onChange({ ...form, num_lotes_depreciar: e.target.value })}
            className="h-11 mt-1.5"
          />
          <p className="text-xs text-muted-foreground mt-0.5">
            La depreciacion mensual se divide entre este numero
          </p>
        </div>
        {form.valor_granja && form.vida_util_anos && (
          <div className="bg-accent rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Depreciacion mensual estimada por lote:</p>
            <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
              ${(
                (parseFloat(form.valor_granja) || 0) /
                ((parseFloat(form.vida_util_anos) || 20) * 12) /
                (parseInt(form.num_lotes_depreciar, 10) || 1)
              ).toFixed(2)}
            </p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button onClick={onSave} disabled={saving} data-testid="btn-guardar-config">
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const CategoriaDialog = ({ open, onOpenChange, value, onChange, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Nueva Categoria de Gasto</DialogTitle>
      </DialogHeader>
      <div className="py-4">
        <Label>Nombre de la categoria</Label>
        <Input
          placeholder="Ej: Transporte, Fumigacion..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 mt-1.5"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button onClick={onSave}>Crear</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const FaseDialog = ({ open, onOpenChange, form, onChange, onSave }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="modal-fase">
      <DialogHeader>
        <DialogTitle>Cambiar Fase del Lote</DialogTitle>
        <CardDescription>
          Al pasar a "Postura", los ingresos de facturas empezaran a descontar la inversion del levante.
        </CardDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Fase</Label>
          <Select value={form.fase} onValueChange={(v) => onChange({ ...form, fase: v })}>
            <SelectTrigger className="h-11 mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Levante">Levante (crecimiento)</SelectItem>
              <SelectItem value="Postura">Postura (produccion)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.fase === 'Postura' && (
          <div>
            <Label>Fecha de inicio de postura</Label>
            <Input
              type="date"
              value={form.fecha_inicio_postura}
              onChange={(e) => onChange({ ...form, fecha_inicio_postura: e.target.value })}
              className="h-11 mt-1.5"
            />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
        <Button onClick={onSave}>Guardar</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
